// 施工箇所一覧(モック準拠)。HierarchicalView で work軸(工種→種別→施工箇所) / location軸(場所区分) の
// 2 サブタブ階層ビュー + 末端5列集計(計画施工箇所数/完了済/計画数量/実績数量/残数量)を表示する。
// 集計・状態ロジックは src/util/areaStatus.ts の純粋関数を再利用(モック extendedStore 由来を Web 正規化スキーマへ移植)。
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Button, IconButton, Collapse, LinearProgress, Tooltip, Alert, Chip, Tabs, Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { PageContext } from "../PageContext";
import { WorkSelectField } from "../../../components/input/WorkSelectField";
import { useFullDb } from "../../../api/dbSlice";
import { useSelectedWork, resolveWorkId } from "../../../util/useSelectedWork";
import { useMountSync } from "../../../util/useMountSync";
import { ContentLoading } from "../../../components/Loading";
import { HierarchicalView, type HierarchyBuilder, type LeafRowInfo } from "../../../components/HierarchicalView";
import { deriveAreaProcessCompletions, type AreaProcessCompletion } from "../../../types/derivedSelectors";
import {
  aggregateProgress, aggregateAreaQuantities, getAreaProcessViews, slotsToText, getAreaSlots,
  progressColor, type AreaQuantityAggregate,
} from "../../../util/areaStatus";
import type { ConstructionPlanAreaBase } from "../../../schema/generated/ConstructionPlanArea";
import { WORD } from "../../../word";
import { renderWord } from "../../../util/renderWord";
import { EmptyState } from "../../../components/EmptyState";

type Axis = "location" | "work";

// 階層ビュー末端(施工箇所)ノードに載せるデータ。
type AreaLeafData = { area: ConstructionPlanAreaBase };

// 工程進捗バー(フォルダ集約 / 施工箇所行 共通)。
function ProcessProgressBar({ completed, total, dense = false }: { completed: number; total: number; dense?: boolean }) {
  if (total === 0) {
    return <Typography variant="caption" color="text.secondary">工程未登録</Typography>;
  }
  const pct = Math.round((completed / total) * 100);
  const color = progressColor(completed, total);
  return (
    <Tooltip title={`完了 ${completed} / 全 ${total} 工程 (${pct}%)`}>
      <Stack gap={0.3} sx={{ minWidth: dense ? 140 : 180 }}>
        <LinearProgress variant="determinate" value={pct} color={color}
          sx={{ height: dense ? 6 : 8, borderRadius: 1, bgcolor: "rgba(0,0,0,0.06)" }} />
        <Typography variant="caption" sx={{ color: `${color}.dark`, fontWeight: 600, lineHeight: 1.2 }}>
          {completed} / {total} 完了 ({pct}%)
        </Typography>
      </Stack>
    </Tooltip>
  );
}

const QTY_EXTRA_COLUMNS = [
  { header: "計画施工箇所数", width: 110, align: "right" as const },
  { header: "完了済", width: 80, align: "right" as const },
  { header: "計画数量", width: 110, align: "right" as const },
  { header: "実績数量", width: 110, align: "right" as const },
  { header: "残数量", width: 110, align: "right" as const },
];

function remainingQtyColor(agg: AreaQuantityAggregate): string {
  if (agg.mixedUnits) {
    return "text.secondary";
  }
  if (agg.remainingQty <= 0) {
    return "success.dark";
  }
  return "text.primary";
}

function numCell(v: number, agg: AreaQuantityAggregate): ReactNode {
  if (agg.mixedUnits) {
    return <Typography variant="caption" color="text.secondary">混在</Typography>;
  }
  return (
    <Typography variant="body2" component="span">
      <b>{Math.round(v * 100) / 100}</b>
      {agg.unit && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{agg.unit}</Typography>}
    </Typography>
  );
}

// aggregate から階層ビューの extraCells 配列(5列)を作る。
function buildQtyExtraCells(agg: AreaQuantityAggregate): ReactNode[] {
  return [
    <Typography variant="body2"><b>{agg.planCount}</b><Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>箇所</Typography></Typography>,
    <Typography variant="body2" color={agg.planCount > 0 && agg.completedCount === agg.planCount ? "success.dark" : "text.primary"}>
      <b>{agg.completedCount}</b>
      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>/ {agg.planCount}</Typography>
    </Typography>,
    numCell(agg.plannedQty, agg),
    numCell(agg.actualQty, agg),
    <Typography variant="body2" color={remainingQtyColor(agg)}>
      {agg.mixedUnits
        ? <Typography component="span" variant="caption" color="text.secondary">混在</Typography>
        : <><b>{Math.round(agg.remainingQty * 100) / 100}</b>{agg.unit && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{agg.unit}</Typography>}</>}
    </Typography>,
  ];
}

export default function AreaListPage({ pageContext }: { pageContext: PageContext }) {
  const { db, loading, reload } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);
  const navigate = useNavigate();

  // IDBは差分アップロード専用同期のため、施工系はサーバ値が落ちてこない。表示前に全件DLして取り込む。
  // 同期完了までローディング表示にする(同期前のローカル内容が一瞬表示されるのを防ぐ)。
  const syncing = useMountSync(pageContext.api.isOnline, () => pageContext.api.entities.syncConstructionData(), reload);
  const [persistedWorkId, setPersistedWorkId] = useSelectedWork();
  const [axis, setAxis] = useState<Axis>("location");
  const [expandedAreaIds, setExpandedAreaIds] = useState<Set<string>>(new Set());

  const toggleExpand = (aid: string) => {
    setExpandedAreaIds((prev) => {
      const next = new Set(prev);
      if (next.has(aid)) {
        next.delete(aid);
      } else {
        next.add(aid);
      }
      return next;
    });
  };

  const activeWorks = useMemo(() => db.works.filter((w) => w.deleted_at == null), [db.works]);
  const workId = resolveWorkId(persistedWorkId, activeWorks);

  // 既定工事へのフォールバック結果を永続化し、他タブ(材料追跡等)と初期選択を揃える。
  useEffect(() => {
    if (workId !== "" && workId !== persistedWorkId) {
      setPersistedWorkId(workId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workId]);

  const completions = useMemo<AreaProcessCompletion[]>(() => deriveAreaProcessCompletions(db), [db.processExecutions]);

  const areas = useMemo<ConstructionPlanAreaBase[]>(() => {
    if (workId === "") {
      return [];
    }
    return db.constructionPlanAreas.filter((a) => a.deleted_at == null && a.work_id === workId);
  }, [db.constructionPlanAreas, workId]);

  const progressDb = { workDetails: db.workDetails };
  const qtyDb = { workSubtypes: db.workSubtypes, workDetails: db.workDetails };
  const processDb = {
    workDetails: db.workDetails,
    mixRatioDefinitions: db.mixRatioDefinitions,
    materials: db.materials,
    plannedMaterials: db.constructionPlanAreaPlannedMaterials,
  };

  // ---- work軸(工種 → 種別 → 施工箇所) ----
  const workAxisBuilder: HierarchyBuilder<AreaLeafData> = (path) => {
    if (path.length === 0) {
      const groups = new Map<number, ConstructionPlanAreaBase[]>();
      for (const a of areas) {
        const key = a.work_type_id ?? 0;
        const arr = groups.get(key) ?? [];
        arr.push(a);
        groups.set(key, arr);
      }
      return {
        title: "工種",
        extraColumns: QTY_EXTRA_COLUMNS,
        rows: Array.from(groups.entries()).map(([wtId, list]) => {
          const prog = aggregateProgress(list, completions, progressDb.workDetails);
          const agg = aggregateAreaQuantities(list, completions, qtyDb);
          return {
            key: String(wtId),
            label: db.workTypes.find((w) => w.id === wtId)?.name ?? "(未分類)",
            count: list.length,
            extraMeta: <ProcessProgressBar completed={prog.completed} total={prog.total} />,
            extraCells: buildQtyExtraCells(agg),
          };
        }),
      };
    }
    if (path.length === 1) {
      const wtId2 = Number(path[0]);
      const filtered = areas.filter((a) => (a.work_type_id ?? 0) === wtId2);
      const sgroups = new Map<number, ConstructionPlanAreaBase[]>();
      for (const a2 of filtered) {
        const sk = a2.work_subtype_id ?? 0;
        const sarr = sgroups.get(sk) ?? [];
        sarr.push(a2);
        sgroups.set(sk, sarr);
      }
      return {
        title: "種別",
        extraColumns: QTY_EXTRA_COLUMNS,
        rows: Array.from(sgroups.entries()).map(([sid, list]) => {
          const prog = aggregateProgress(list, completions, progressDb.workDetails);
          const agg = aggregateAreaQuantities(list, completions, qtyDb);
          return {
            key: String(sid),
            label: db.workSubtypes.find((w) => w.id === sid)?.name ?? "(未分類)",
            count: list.length,
            extraMeta: <ProcessProgressBar completed={prog.completed} total={prog.total} />,
            extraCells: buildQtyExtraCells(agg),
          };
        }),
      };
    }
    if (path.length === 2) {
      const wtId3 = Number(path[0]);
      const sid3 = Number(path[1]);
      const leaves = areas.filter((a) => (a.work_type_id ?? 0) === wtId3 && (a.work_subtype_id ?? 0) === sid3);
      return {
        title: "施工箇所",
        rows: leaves.map((a) => ({
          key: a.id,
          label: slotsToText(a.id, db.constructionPlanAreaLocations, db.locationDivisions) || "(場所未設定)",
          navigable: false,
          data: { area: a },
        })),
      };
    }
    return null;
  };

  // ---- location軸(場所区分スロット) ----
  // 各 area の slot→location_division_id を引いて、深さごとに区分でグルーピングする。
  const locationAxisBuilder: HierarchyBuilder<AreaLeafData> = (path) => {
    const slotDivOf = (areaId: string, slot: number): number | null => {
      const hit = getAreaSlots(areaId, db.constructionPlanAreaLocations).find((s) => s.slot === slot);
      return hit?.location_division_id ?? null;
    };
    const filtered = areas.filter((a) => {
      for (let i = 0; i < path.length; i++) {
        const slot = i + 1;
        const expected = path[i] === "none" ? null : Number(path[i]);
        if (slotDivOf(a.id, slot) !== expected) {
          return false;
        }
      }
      return true;
    });
    const nextSlot = path.length + 1;
    const groups = new Map<string, ConstructionPlanAreaBase[]>();
    for (const a of filtered) {
      const div = slotDivOf(a.id, nextSlot);
      const k = div == null ? "none" : String(div);
      const arr = groups.get(k) ?? [];
      arr.push(a);
      groups.set(k, arr);
    }
    const allLeavesNow = nextSlot > 10
      || (groups.size === 1 && groups.has("none"))
      || filtered.every((a) => {
        for (let s = nextSlot; s <= 10; s++) {
          if (slotDivOf(a.id, s) != null) {
            return false;
          }
        }
        return true;
      });
    if (allLeavesNow) {
      return {
        title: "施工箇所",
        rows: filtered.map((a) => ({
          key: a.id,
          label: slotsToText(a.id, db.constructionPlanAreaLocations, db.locationDivisions) || "(場所未設定)",
          navigable: false,
          data: { area: a },
        })),
      };
    }
    return {
      title: `区分${nextSlot}`,
      extraColumns: QTY_EXTRA_COLUMNS,
      rows: Array.from(groups.entries()).sort((x, y) => x[0].localeCompare(y[0])).map(([k, list]) => {
        const prog = aggregateProgress(list, completions, progressDb.workDetails);
        const agg = aggregateAreaQuantities(list, completions, qtyDb);
        const label = k === "none" ? "(未設定)" : (db.locationDivisions.find((d) => d.id === Number(k))?.division_name ?? k);
        return {
          key: k,
          label: label,
          count: list.length,
          extraMeta: <ProcessProgressBar completed={prog.completed} total={prog.total} />,
          extraCells: buildQtyExtraCells(agg),
        };
      }),
    };
  };

  const newAreaParams = (currentPath: { key: string; label: string }[], isLocation: boolean): string => {
    const params = new URLSearchParams();
    if (workId !== "") {
      params.set("work_id", String(workId));
    }
    if (isLocation) {
      currentPath.forEach((p, i) => {
        if (p.key !== "none") {
          params.set(`loc${i + 1}`, p.key);
        }
      });
    } else {
      if (currentPath[0]) {
        params.set("work_type_id", currentPath[0].key);
      }
      if (currentPath[1] && currentPath[1].key !== "none") {
        params.set("work_subtype_id", currentPath[1].key);
      }
    }
    return params.toString();
  };

  const renderLeavesTable = (rows: LeafRowInfo<AreaLeafData>[]) => {
    return (
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", width: 40, p: 0.5 }} />
              <TableCell sx={{ fontWeight: "bold" }}>場所区分</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 110 }}>工種</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 120 }}>種別</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 180 }}>工程進捗</TableCell>
              {QTY_EXTRA_COLUMNS.map((col, ci) => (
                <TableCell key={ci} sx={{ fontWeight: "bold", width: col.width }} align={col.align}>{col.header}</TableCell>
              ))}
              <TableCell sx={{ fontWeight: "bold", width: 100 }} align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const area = r.node.data?.area;
              if (!area) {
                return null;
              }
              const wt = db.workTypes.find((w) => w.id === area.work_type_id);
              const wst = db.workSubtypes.find((w) => w.id === area.work_subtype_id);
              const processes = getAreaProcessViews(area, completions, processDb);
              const completedCount = processes.filter((p) => p.isCompleted).length;
              const expanded = expandedAreaIds.has(area.id);
              const qtyCells = buildQtyExtraCells(aggregateAreaQuantities([area], completions, qtyDb));
              const locText = slotsToText(area.id, db.constructionPlanAreaLocations, db.locationDivisions);
              return (
                <Fragment key={r.node.key}>
                  <TableRow hover sx={{
                    cursor: "pointer", borderLeft: 4, borderLeftColor: "success.main",
                    bgcolor: expanded ? "rgba(46,125,50,0.08)" : "rgba(46,125,50,0.04)",
                    "&:hover": { bgcolor: "rgba(46,125,50,0.10)" },
                    "& > td": { borderBottom: expanded ? "none" : undefined },
                  }} onClick={() => toggleExpand(area.id)}>
                    <TableCell sx={{ p: 0.5 }}>
                      <IconButton size="small">{expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}</IconButton>
                    </TableCell>
                    <TableCell>
                      {locText === ""
                        ? <Typography variant="caption" color="text.secondary">(場所未設定)</Typography>
                        : (
                          <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                            {locText.split(" / ").map((part, j, parts) => (
                              <span key={j} style={{ display: "inline-flex", alignItems: "center" }}>
                                <Typography variant="body2" color="text.primary">{part}</Typography>
                                {j < parts.length - 1 && <NavigateNextIcon fontSize="small" sx={{ color: "text.disabled", mx: 0.25 }} />}
                              </span>
                            ))}
                          </Stack>
                        )}
                    </TableCell>
                    <TableCell><Typography variant="body2">{wt?.name ?? "-"}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{wst?.name ?? "-"}</Typography></TableCell>
                    <TableCell sx={{ py: 0.5 }}><ProcessProgressBar completed={completedCount} total={processes.length} dense /></TableCell>
                    {qtyCells.map((cell, ci) => (
                      <TableCell key={ci} align={QTY_EXTRA_COLUMNS[ci]?.align} sx={{ py: 0.5 }}>{cell}</TableCell>
                    ))}
                    <TableCell align="right">
                      <Button size="small" variant="outlined" endIcon={<ArrowForwardIcon fontSize="small" />} sx={{ whiteSpace: "nowrap" }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/console/construction-plan/area/${area.id}/`); }}>詳細</Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6 + QTY_EXTRA_COLUMNS.length} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: "grey.50" }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>この施工箇所の工程一覧</Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: 120 }}>状態</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: 200 }}>完了時刻</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {processes.length === 0 ? (
                                <TableRow><TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>工程マスタが登録されていません</TableCell></TableRow>
                              ) : processes.map((p) => (
                                <TableRow key={p.workDetailId}>
                                  <TableCell>{p.workDetailName}</TableCell>
                                  <TableCell>
                                    <Chip size="small" color={p.isCompleted ? "success" : "default"} variant={p.isCompleted ? "filled" : "outlined"}
                                      icon={p.isCompleted ? <CheckCircleIcon /> : undefined} label={p.isCompleted ? "完了" : "未完了"} />
                                  </TableCell>
                                  <TableCell>
                                    {p.completedAt
                                      ? <Typography variant="body2">{p.completedAt}</Typography>
                                      : <Typography variant="caption" color="text.secondary">-</Typography>}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  if (loading || syncing) {
    return (
      <Stack gap={2}>
        <Typography variant="h5" fontWeight="bold">施工箇所一覧</Typography>
        <ContentLoading />
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight="bold">施工箇所一覧</Typography>
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <WorkSelectField options={activeWorks.map((w) => ({ id: w.id, label: w.short_name || w.official_name }))} value={workId} onChange={setPersistedWorkId} />
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate(`/console/construction-plan/area/new/${workId !== "" ? `?work_id=${workId}` : ""}`)}>新規施工箇所</Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.constructionPlan.listInfo)}</Alert>

      <Paper variant="outlined">
        <Tabs value={axis} onChange={(_, v) => setAxis(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab value="location" label="場所区分別進捗" />
          <Tab value="work" label="施工方法別進捗" />
        </Tabs>
        <Box p={2}>
          {workId === "" && <EmptyState message="工事を選択してください" py={4} />}
          {workId !== "" && areas.length === 0 && <EmptyState message="該当する施工箇所がありません" py={4} />}
          {workId !== "" && areas.length > 0 && axis === "work" && (
            <HierarchicalView builder={workAxisBuilder} renderLeaves={renderLeavesTable}
              actionPerLevel={(p) => (
                <Button size="small" variant="outlined" startIcon={<AddIcon />}
                  onClick={() => navigate(`/console/construction-plan/area/new/?${newAreaParams(p, false)}`)}>この文脈で新規施工箇所</Button>
              )} />
          )}
          {workId !== "" && areas.length > 0 && axis === "location" && (
            <HierarchicalView builder={locationAxisBuilder} renderLeaves={renderLeavesTable}
              actionPerLevel={(p) => (
                <Button size="small" variant="outlined" startIcon={<AddIcon />}
                  onClick={() => navigate(`/console/construction-plan/area/new/?${newAreaParams(p, true)}`)}>この文脈で新規施工箇所</Button>
              )} />
          )}
        </Box>
      </Paper>
    </Stack>
  );
}
